import React, { useState, useRef, useCallback, useEffect } from "react";

interface ResizablePanelProps {
  children: React.ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  position: "left" | "right";
  className?: string;
  onWidthChange?: (_width: number) => void;
}

export const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  defaultWidth = 300,
  minWidth = 200,
  maxWidth = 800,
  position = "right",
  className = "",
  onWidthChange,
}) => {
  const [width, setWidth] = useState(defaultWidth);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startMouseX = useRef<number>(0);
  const startWidth = useRef<number>(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startMouseX.current = e.clientX;
      startWidth.current = width;

      // Add cursor style to body to maintain resize cursor during drag
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    },
    [width],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startMouseX.current;
      const newWidth =
        position === "right"
          ? startWidth.current - deltaX // For right panel, moving left increases width
          : startWidth.current + deltaX; // For left panel, moving right increases width

      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

      setWidth(constrainedWidth);
      onWidthChange?.(constrainedWidth);
    },
    [isDragging, position, minWidth, maxWidth, onWidthChange],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [isDragging]);

  // Global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const panelStyle: React.CSSProperties = {
    width: `${width}px`,
    minWidth: `${minWidth}px`,
    maxWidth: `${maxWidth}px`,
  };

  const dragHandleStyle: React.CSSProperties = {
    cursor: "ew-resize",
    width: "4px",
    backgroundColor: isDragging ? "#3b82f6" : "transparent",
    transition: isDragging ? "none" : "background-color 0.2s ease",
  };

  return (
    <div
      ref={panelRef}
      className={`relative flex ${position === "right" ? "flex-row" : "flex-row-reverse"} ${className}`}
      style={panelStyle}
    >
      {/* Drag handle */}
      <div
        className={`
          flex-shrink-0 flex items-center justify-center group
          ${position === "right" ? "border-l border-gray-700" : "border-r border-gray-700"}
          hover:bg-gray-700/30 transition-colors duration-200
        `}
        style={dragHandleStyle}
        onMouseDown={handleMouseDown}
      >
        {/* Visual drag handle indicator */}
        <div className="h-8 w-1 bg-gray-500 group-hover:bg-gray-400 transition-colors duration-200 rounded-full" />
      </div>

      {/* Panel content */}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};
